/**
 * @file SignIn.tsx
 * @description Splash page for user login and sign-up, integrating with Firebase Authentication.
 */

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useResizeObserver } from "../../hooks/useResizeObserver";
import Login from "../../components/auth/Login";
import SignUp from "../../components/auth/SignUp";
import LoginIllustration from "../../components/common/LoginIllustration";
import SignUpIllustration from "../../components/common/SignUpIllustration";
import AlphabetBackground from "../../components/common/AlphabetBackground";
import log from "../../config/logging";

const SignIn: React.FC<{ showSignUp?: boolean }> = ({ showSignUp = false }) => {
  const [isSignUp, setIsSignUp] = useState(showSignUp);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setIsSignUp(location.pathname === "/registrati");
  }, [location]);

  const handleSwitchMode = () => {
    const newPath = isSignUp ? "/accedi" : "/registrati";
    navigate(newPath);
  };

  const onAuthSuccess = () => {
    log.debug("Auth success, navigating to home");
    navigate("/");
  };

  const containerRef = useRef<HTMLDivElement>(null);
  const { height } = useResizeObserver(containerRef);

  useEffect(() => {
    const mainElement = document.querySelector("#asw > main") as HTMLElement;
    if (mainElement) {
      const originalClasses = mainElement.className;

      mainElement.classList.remove("!mt-2", "!mb-32");
      mainElement.classList.add("!my-0", "!py-0");

      if (height) {
        mainElement.style.height = `${height}px`;
      }

      return () => {
        mainElement.className = originalClasses;
        mainElement.style.height = "";
      };
    }
  }, [height]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-x-0 z-30 flex flex-col min-h-screen lg:flex-row"
    >
      <div className="flex flex-col justify-center w-full p-8 bg-gray-100 lg:w-1/2">
        <div className="px-[10vw] lg:px-[5vw] 2xl:px-[7.5vw] text-center">
          {isSignUp ? (
            <>
              <h2 className="mb-4 text-4xl font-medium text-primary">
                Benvenuto!
              </h2>
              <p className="mb-12 text-lg">
                Crea un account gratuito e prova il nostro Content Generator:
                potrai generare fino a 4 contenuti SEO ogni mese.
              </p>
              <SignUp
                onSuccess={onAuthSuccess}
                switchToLogin={handleSwitchMode}
              />
            </>
          ) : (
            <>
              <h2 className="mb-12 text-4xl font-medium text-primary">
                Bentornato!
              </h2>
              <Login
                onSuccess={onAuthSuccess}
                switchToSignUp={handleSwitchMode}
              />
            </>
          )}
        </div>
      </div>
      <div className="relative items-center justify-center hidden w-1/2 lg:flex">
        <AlphabetBackground />
        <div className="relative flex items-center justify-center w-3/4 h-3/4">
          {isSignUp ? <SignUpIllustration /> : <LoginIllustration />}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
